/**
 * Shared GSTIN selector — used on Sales invoice header, Purchase bill header,
 * MIS Pack filter, and Compliance Management pages.
 *
 * Fetches the current user's allowed GSTINs from /api/company/my-gstins.
 * Emits `{ gstin, label, state_cd, is_default, legal_name }` on change.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

export default function GstinSelector({ value, onChange, label = "Company GSTIN",
                                          size = "sm", showLegalName = false, testid = "gstin-selector" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    api.get("/company/my-gstins").then((r) => {
      if (dead) return;
      const arr = r.data.items || [];
      setItems(arr);
      if (!value && r.data.default) onChange?.(r.data.default);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="text-xs text-muted-foreground">Loading GSTINs…</div>;
  if (!items.length) {
    return (
      <div className="text-xs text-amber-600" data-testid={`${testid}-empty`}>
        No GSTINs configured. Go to <b>Settings → Company GSTINs</b> to add one.
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      {label && <Label className="text-xs whitespace-nowrap">{label}:</Label>}
      <select
        value={value?.gstin || ""}
        onChange={(e) => {
          const g = items.find((x) => x.gstin === e.target.value);
          onChange?.(g);
        }}
        className={`border rounded px-2 py-1 bg-background font-mono text-xs ${
          size === "lg" ? "min-w-[260px]" : "min-w-[200px]"
        }`}
        data-testid={`${testid}-select`}
      >
        {items.map((g) => (
          <option key={g.gstin} value={g.gstin}>
            {g.gstin} {g.label ? `· ${g.label}` : ""}
            {g.session_active ? " ✓" : ""}
            {g.is_default ? " (default)" : ""}
          </option>
        ))}
      </select>
      {value && !value.session_active && (
        <span className="text-xs text-red-500 whitespace-nowrap">
          Not authenticated — go to GST Reports first
        </span>
      )}
      {showLegalName && value?.legal_name && (
        <span className="text-xs text-muted-foreground italic">{value.legal_name}</span>
      )}
      {value?.verified && (
        <CheckCircle2 className="w-3 h-3 text-emerald-500" title="Verified on gst.gov.in" />
      )}
    </div>
  );
}
